import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import './ModalPodSessions.css'
import {format} from 'date-fns'

const ModalPodSessions = ({closeModal, podIdSessions, podInfo}) => {
    const podId = podIdSessions;
    const [podSessions, setPodSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState('');
    const summarySectionRef = useRef(null);
    const sessionsTableRef = useRef(null);
    const [contentWidthPx, setContentWidthPx] = useState(null);

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setErrors('');

        fetch(process.env.REACT_APP_API_URL_BASE + '/protocol_logs/machine/' + podId + '/view',
            {
                method: 'GET',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            })
            .then(res => {
                if (!res.ok) {
                    throw new Error('Unable to load session history.');
                }

                return res.json();
            })
            .then(data => {
                setPodSessions(Array.isArray(data) ? data : [])
            })
            .catch(error => {
                console.error(error);
                setErrors('Unable to load session history right now.');
            })
            .finally(() => {
                setIsLoading(false);
            });
        }, [podId]);

    const handleSubmit = (e) => {
        e.preventDefault();
        closeModal();
    }

    const showLoadedContent = !isLoading;

    const buildUsageSummary = useCallback((items, valueSelector) => {
        const usageMap = items.reduce((accumulator, item) => {
            const rawValue = valueSelector(item);
            const normalizedValue = String(rawValue ?? '').trim() || 'Unknown';
            accumulator[normalizedValue] = (accumulator[normalizedValue] || 0) + 1;
            return accumulator;
        }, {});

        return Object.entries(usageMap)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => {
                if (b.count !== a.count) {
                    return b.count - a.count;
                }
                return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
            });
    }, []);

    const protocolUsage = useMemo(
        () => buildUsageSummary(podSessions, (item) => item?.name),
        [buildUsageSummary, podSessions]
    );

    const clientUsage = useMemo(
        () => buildUsageSummary(podSessions, (item) => `${item?.client_first_name ?? ''} ${item?.client_last_name ?? ''}`.trim()),
        [buildUsageSummary, podSessions]
    );

    const getUsageSizing = useCallback((usageItems, labelHeader) => {
        const maxLabelChars = usageItems.reduce(
            (maxChars, item) => Math.max(maxChars, String(item.label ?? '').length),
            labelHeader.length
        );
        const maxCountChars = usageItems.reduce(
            (maxChars, item) => Math.max(maxChars, String(item.count ?? '').length),
            'Count'.length
        );

        const labelWidthCh = Math.max(8, maxLabelChars + 1);
        const countWidthCh = Math.max(5, maxCountChars + 1);

        return {
            labelWidthCh,
            countWidthCh,
        };
    }, []);

    const protocolUsageSizing = useMemo(
        () => getUsageSizing(protocolUsage, 'Protocol'),
        [getUsageSizing, protocolUsage]
    );

    const clientUsageSizing = useMemo(
        () => getUsageSizing(clientUsage, 'Client'),
        [clientUsage, getUsageSizing]
    );

    const lastSessionDate = useMemo(() => {
        if (!Array.isArray(podSessions) || podSessions.length === 0) {
            return null;
        }

        const latestTimestamp = podSessions.reduce((latest, session) => {
            const timestamp = Date.parse(session?.started);
            if (Number.isNaN(timestamp)) {
                return latest;
            }
            return Math.max(latest, timestamp);
        }, Number.NEGATIVE_INFINITY);

        if (!Number.isFinite(latestTimestamp)) {
            return null;
        }

        return format(new Date(latestTimestamp), 'MMM d, yyyy');
    }, [podSessions]);

    const columnSizing = useMemo(() => {
        const measure = (value) => String(value ?? '').length;
        const rows = Array.isArray(podSessions) ? podSessions : [];

        const columns = [
            {
                key: 'date',
                header: 'Date',
                className: 'center',
                value: (row) => format(new Date(row.started), 'MMM d, yyyy'),
                minCh: 12,
                maxCh: 14,
            },
            {
                key: 'client',
                header: 'Client',
                className: 'left',
                value: (row) => `${row.client_first_name ?? ''} ${row.client_last_name ?? ''}`.trim(),
                minCh: 12,
                maxCh: 30,
            },
            {
                key: 'mins',
                header: 'Mins',
                className: 'center',
                value: (row) => row.total_mins,
                minCh: 6,
                maxCh: 8,
            },
            {
                key: 'protocol',
                header: 'Protocol',
                className: 'left',
                value: (row) => row.name,
                minCh: 12,
                maxCh: 30,
            },
        ];

        const sizedColumns = columns.map((column) => {
            const longestContent = rows.reduce((longest, row) => {
                return Math.max(longest, measure(column.value(row)));
            }, measure(column.header));

            const paddedCh = longestContent + 1;
            const widthCh = Math.max(column.minCh, Math.min(column.maxCh, paddedCh));

            return {
                ...column,
                widthCh,
            };
        });

        const tableWidthCh = sizedColumns.reduce((total, column) => total + column.widthCh, 0);

        return {
            columns: sizedColumns,
            tableWidthCh,
        };
    }, [podSessions]);

    const summaryWidthCh = useMemo(() => {
        const protocolCardWidthCh = protocolUsageSizing.labelWidthCh + protocolUsageSizing.countWidthCh + 9;
        const clientCardWidthCh = clientUsageSizing.labelWidthCh + clientUsageSizing.countWidthCh + 9;
        const cardsGapCh = 4;
        return protocolCardWidthCh + clientCardWidthCh + cardsGapCh;
    }, [clientUsageSizing, protocolUsageSizing]);

    const modalContentWidthCh = Math.max(columnSizing.tableWidthCh, summaryWidthCh);

    useLayoutEffect(() => {
        const measureLayout = () => {
            const summaryWidth = showLoadedContent && !errors && summarySectionRef.current
                ? summarySectionRef.current.scrollWidth
                : 0;
            const tableWidth = sessionsTableRef.current ? sessionsTableRef.current.scrollWidth : 0;
            const measuredWidth = Math.max(summaryWidth, tableWidth);

            if (measuredWidth > 0) {
                setContentWidthPx(Math.ceil(measuredWidth));
            }
        };

        measureLayout();

        const rafId = window.requestAnimationFrame(() => {
            measureLayout();
        });

        window.addEventListener('resize', measureLayout);

        return () => {
            window.cancelAnimationFrame(rafId);
            window.removeEventListener('resize', measureLayout);
        };
    }, [
        clientUsageSizing.countWidthCh,
        clientUsageSizing.labelWidthCh,
        errors,
        podSessions.length,
        protocolUsageSizing.countWidthCh,
        protocolUsageSizing.labelWidthCh,
        showLoadedContent,
    ]);

    const modalWidthStyle = {
        width: contentWidthPx
            ? `min(calc(${contentWidthPx}px + 3.5rem), 96vw)`
            : `min(calc(${modalContentWidthCh}ch + 5.4rem), 96vw)`,
    };

    const tableWidthStyle = {
        width: `${columnSizing.tableWidthCh}ch`,
    };

    return (
    <div className='modal-table modal-pod-sessions-overlay' onClick={(e) => {
        if (e.target.classList.contains('modal-table')) {
            closeModal()
        }
    }}>
            <div className='modal-sessions modal-pod-sessions' style={modalWidthStyle}>
                <div className='modal-sessions-header'>
                    {showLoadedContent ? (
                        <>
                            <h3>Session History</h3>
                            <p>
                                {podInfo?.description ?? podInfo?.pod_name ?? 'Pod'} | Pod ID: {podInfo?.id}
                            </p>
                            <div className='modal-sessions-chip-row'>
                                <div className='modal-sessions-count'>
                                    {podSessions.length} {podSessions.length === 1 ? 'session' : 'sessions'}
                                </div>
                                <div className='modal-sessions-count'>Last Session: {lastSessionDate ?? 'N/A'}</div>
                            </div>
                        </>
                    ) : (
                        <div className='modal-sessions-header-skeleton'>
                            <span className='table-skeleton-line modal-header-skeleton-title' />
                            <span className='table-skeleton-line modal-header-skeleton-subtitle' />
                            <span className='table-skeleton-line modal-header-skeleton-pill' />
                        </div>
                    )}
                </div>

                {errors && <div className='error'>{errors}</div>}

                {showLoadedContent && !errors && (
                    <div className='modal-sessions-usage-summary' ref={summarySectionRef} style={{ flexWrap: 'nowrap', overflow: 'visible' }}>
                        <div className='usage-card'>
                            <div className='usage-card-title'>Protocols ({protocolUsage.length})</div>
                            <div className='usage-table-wrap' style={{ maxHeight: '10.2rem', overflowY: 'auto', overflowX: 'hidden' }}>
                                {protocolUsage.length > 0 ? (
                                    <table className='usage-table'>
                                        <colgroup>
                                            <col style={{ width: `${protocolUsageSizing.labelWidthCh}ch` }} />
                                            <col style={{ width: `${protocolUsageSizing.countWidthCh}ch` }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className='left'>Protocol</th>
                                                <th className='center'>Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {protocolUsage.map((item) => (
                                                <tr key={`pod-protocol-usage-${item.label}`}>
                                                    <td className='left'>{item.label}</td>
                                                    <td className='center usage-count'>{item.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <span className='usage-empty'>No protocol usage found.</span>
                                )}
                            </div>
                        </div>

                        <div className='usage-card'>
                            <div className='usage-card-title'>Clients ({clientUsage.length})</div>
                            <div className='usage-table-wrap' style={{ maxHeight: '10.2rem', overflowY: 'auto', overflowX: 'hidden' }}>
                                {clientUsage.length > 0 ? (
                                    <table className='usage-table'>
                                        <colgroup>
                                            <col style={{ width: `${clientUsageSizing.labelWidthCh}ch` }} />
                                            <col style={{ width: `${clientUsageSizing.countWidthCh}ch` }} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th className='left'>Client</th>
                                                <th className='center'>Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {clientUsage.map((item) => (
                                                <tr key={`pod-client-usage-${item.label}`}>
                                                    <td className='left'>{item.label}</td>
                                                    <td className='center usage-count'>{item.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <span className='usage-empty'>No client usage found.</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className='table-wrapper modal-sessions-table-scroll'>
                    <table className='table-sessions' style={tableWidthStyle} ref={sessionsTableRef}>
                        <colgroup>
                            {columnSizing.columns.map((column) => (
                                <col key={column.key} style={{ width: `${column.widthCh}ch` }} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                {columnSizing.columns.map((column) => (
                                    <th key={column.key} className={column.className}>
                                        {column.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array.from({ length: 7 }).map((_, rowIndex) => (
                                    <tr key={`pod-sessions-skeleton-${rowIndex}`} className='table-skeleton-row'>
                                        {columnSizing.columns.map((column) => (
                                            <td key={`${column.key}-${rowIndex}`}>
                                                <span className={`table-skeleton-line${column.key === 'date' || column.key === 'mins' ? ' short' : ''}`} />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : podSessions.length > 0 ? (
                                podSessions.map((podSession, i) => {
                                    return <tr key={i}>
                                        <td className='center'>{format(new Date(podSession.started), 'MMM d, yyyy')}</td>
                                        <td className='left'>{podSession.client_first_name} {podSession.client_last_name}</td>
                                        <td className='center'>{podSession.total_mins}</td>
                                        <td className='left'>{podSession.name}</td>
                                    </tr>
                                })
                            ) : (
                                <tr>
                                    <td className='center modal-sessions-empty' colSpan={4}>No session history found for this pod.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <button type='submit' className='btn' onClick={handleSubmit}>Close</button>
            </div>
        </div>
  )
}

export default ModalPodSessions